import { useCallback } from "react";
import type { Dispatch, SetStateAction } from "react";
import { AppType, ChatAppResponse, TreeNode, getThoughtsApi, updateTreeAnswerByQuestionId } from "../../../api";
import { AnalysisPanelTabs } from "../../../components/AnalysisPanel";

interface UseThoughtsLoaderParams {
    answers: any[];
    answerIndexMap: Record<string, number>;
    sourcePage: "chat" | "feedback";
    setAnswers: Dispatch<SetStateAction<any[]>>;
    setRegeneratedAnswers: Dispatch<SetStateAction<Record<string, Record<string, ChatAppResponse[]>>>>;
    setAllAnswersTree: Dispatch<SetStateAction<TreeNode[]>>;
    setThoughtsLoadingIndex: (index: number | null) => void;
    setActiveAnalysisPanelTab: (tab: AnalysisPanelTabs | undefined) => void;
    setSelectedAnswer: (index: number) => void;
}

/**
 * Fetches thoughts for a given answer and stores them in all three state structures
 * (answers, regeneratedAnswers, allAnswersTree) so switching versions or tabs
 * never requires a repeat backend call.
 */
export const useThoughtsLoader = ({
    answers,
    answerIndexMap,
    sourcePage,
    setAnswers,
    setRegeneratedAnswers,
    setAllAnswersTree,
    setThoughtsLoadingIndex,
    setActiveAnalysisPanelTab,
    setSelectedAnswer
}: UseThoughtsLoaderParams) => {
    const loadThoughts = useCallback(
        async (index: number, appTypeForPanel: AppType, answerForType: ChatAppResponse | undefined) => {
            const conv_id = answerForType?.context?.conv_id;
            if (!conv_id) return false;

            const questionId = answers[index]?.[0]?.question_id as string | undefined;
            const activeVersionIndex = questionId ? (answerIndexMap[`${questionId}__${appTypeForPanel}`] ?? 0) : 0;

            setActiveAnalysisPanelTab(AnalysisPanelTabs.ThoughtProcessTab);
            setSelectedAnswer(index);

            try {
                setThoughtsLoadingIndex(index);
                const response = await getThoughtsApi(conv_id, sourcePage);
                if (!response.ok) {
                    console.error("Failed to fetch thoughts: HTTP", response.status);
                    return false;
                }
                const fetchedResult = await response.json();
                if (fetchedResult.error || typeof fetchedResult?.thoughts !== "string") {
                    console.error("Failed to fetch thoughts: unexpected payload", fetchedResult);
                    return false;
                }
                const fetchedThoughts = fetchedResult.thoughts;

                // 1. Update answers state
                setAnswers(prev => {
                    const updatedAnswers = [...prev];
                    const entry = updatedAnswers[index];
                    if (!entry) return prev;
                    const appResponse = entry[1]?.[appTypeForPanel];
                    if (!appResponse?.context) return prev;
                    updatedAnswers[index] = [
                        entry[0],
                        {
                            ...entry[1],
                            [appTypeForPanel]: {
                                ...appResponse,
                                context: {
                                    ...appResponse.context,
                                    thoughts: fetchedThoughts
                                }
                            }
                        }
                    ];
                    return updatedAnswers;
                });

                if (questionId) {
                    // 2. Update regeneratedAnswers state
                    setRegeneratedAnswers(prev => {
                        const versions = prev[questionId]?.[appTypeForPanel];
                        if (!versions || versions.length === 0) return prev;
                        const targetVersion = versions[activeVersionIndex];
                        if (!targetVersion?.context) return prev;
                        const updatedVersions = [...versions];
                        updatedVersions[activeVersionIndex] = {
                            ...targetVersion,
                            context: {
                                ...targetVersion.context,
                                thoughts: fetchedThoughts
                            }
                        };
                        return {
                            ...prev,
                            [questionId]: {
                                ...prev[questionId],
                                [appTypeForPanel]: updatedVersions
                            }
                        };
                    });

                    // 3. Update allAnswersTree so applyVersionChange also gets thoughts
                    if (answerForType?.context) {
                        const updatedResponse = {
                            ...answerForType,
                            context: {
                                ...answerForType.context,
                                thoughts: fetchedThoughts
                            }
                        };
                        setAllAnswersTree(prev => updateTreeAnswerByQuestionId(prev as any, questionId, appTypeForPanel, updatedResponse));
                    }
                }

                return true;
            } catch (e) {
                console.error("Failed to fetch thoughts:", e);
                return false;
            } finally {
                setThoughtsLoadingIndex(null);
            }
        },
        [
            answers,
            answerIndexMap,
            sourcePage,
            setAnswers,
            setRegeneratedAnswers,
            setAllAnswersTree,
            setThoughtsLoadingIndex,
            setActiveAnalysisPanelTab,
            setSelectedAnswer
        ]
    );

    return { loadThoughts };
};
